import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Permission, SalesOrderType, Res, PaymentMethodDTO } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import * as SalesStore from '../SalesStore';
import { ActionButtonDefinition, InfoCard, Keypad, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import { createEndTakeOutOrderData, EndTakeOutOrderData, toTakeOutOrderParams } from './EndTakeOutOrderData';
import { CashdrawerFacade, CustomerFacade, SalesOrderFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { Flex, Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { legacyControlsFor } from 'core/ui-next/controls';

type Props = {
	orderData: EndTakeOutOrderData,
	availablePaymentMethods: PaymentMethodDTO[]
} & Modal.Actions<EndTakeOutOrderData>;

class EndTakeOutOrder extends PosScreenClassComponent<Props, EndTakeOutOrderData> {

	private form = React.createRef<UI.Form<EndTakeOutOrderData>>();

	protected getScreenId(): string {
		return 'END_TAKEOUT_ORDER';
	}

	protected getTitle(): string {
		return Res.Web.END_TAKE_OUT_ORDER();
	}

	protected getImage(): string | SvgIcon {
		return Images.EndTakeOutOrder;
	}

	protected getSize(): ScreenSize {
		return { width: 900, height: 650 };
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOk });
	}

	protected useKeypad(): boolean {
		return false;
	}

	private onOk = async () => {
		const updated = await this.form.current!.updateModel();
		if (updated && (await checkPreconditions(this.state))) {
			this.close(this.state);
		}
	};

	private onTypeChanged = async () => {
		const isValid = await this.form.current?.updateModel();
		if (isValid) {
			this.forceUpdate();
		}
	};

	protected getContent(): React.ReactNode {
		const { Form, TextBox, ComboBox, DecimalBox, TimeBox } = UI.controlsFor<EndTakeOutOrderData>();
		const { DatePicker } = legacyControlsFor<EndTakeOutOrderData>();

		const order = this.props.orderData;

		return <Flex.Horizontal >
			<Flex.Item size='*' style={{ overflowX: 'hidden' }}>
				<Cols className='m-b-lg m-t-md m-l-sm'>
					<Col width='*' >
						<Form ref={this.form} model={this.state}>
							<ComboBox label={Res.Web.END_TAKEOUT_ORDER_TYPE_LBL()} linkTo={x => x.type} options={[SalesOrderType.Delivery, SalesOrderType.TakeAway]} format={SalesOrderType.toString} onValueChanged={this.onTypeChanged} required />
							<DatePicker label={Res.Web.END_TAKEOUT_ORDER_DATE_LBL()} linkTo={x => x.deliveryDate} required />
							<TimeBox label={Res.Web.END_TAKEOUT_ORDER_TIME_LBL()} linkTo={x => x.deliveryTime} placeholder={'HH:mm'} required />
							{this.state.type === SalesOrderType.Delivery
								? <>
									<ComboBox linkTo={x => x.preferredPaymentMethod} label={Res.Web.END_TAKE_OUT_ORDER_PREFERRED_PAYMENT_METHOD_LBL()} options={this.props.availablePaymentMethods} placeholder={Res.Web.END_TAKE_OUT_ORDER_PREFERRED_PAYMENT_METHOD_PLACEHOLDER()} />
									<DecimalBox linkTo={x => x.changeNeeded} precision={2} label={Res.Web.END_TAKE_OUT_ORDER_CHANGE_LBL()} />
								</>
								: null}
							<TextBox linkTo={x => x.notes} lines={3} placeholder={Res.Web.END_TAKE_OUT_ORDER_NOTES_PLACEHOLDER()} />
						</Form>
					</Col>
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<Flex.Vertical>
					<Flex.Item size='*' style={{ overflowX: 'hidden' }}>
						<InfoCard Icon='fa fa-id-card-o' title={order.customerFiscalName} lines={[order.customerPhone, order.customerEmail, order.contactName]} />
						<InfoCard Icon='fa fa-building-o' title={order.address.Street} lines={[`${order.address.ZipCode} ${order.address.Region}`]} />
					</Flex.Item>
					<Flex.Item>
						<Keypad isFullScreen={false} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>
		</Flex.Horizontal>;
	}

	createInitialState(props: Props): Nullable<EndTakeOutOrderData> {
		return { ...props.orderData };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}
}

async function checkPreconditions(data: EndTakeOutOrderData): Promise<boolean> {

	if (data.netAmount < 0) {
		await Message.show(Res.Msg.MSG_CANNOT_CLOSE_TICKET_WITH_NEGATIVE_AMOUNT());
		return false;
	}

	if (!data.customerPhone) {
		await Message.show(Res.Msg.MSG_CANNOT_CREATE_DELIVERY_ORDER_WITHOUT_PHONE());
		return false;
	}

	if (data.type === SalesOrderType.Delivery && !data.address.Street) {
		await Message.show(Res.Msg.MSG_CANNOT_CREATE_DELIVERY_ORDER());
		return false;
	}

	if (data.preferredPaymentMethod?.IsCash && SessionProvider.getCurrent().MasterHasCashControl) {
		await Message.show(Res.Msg.MSG_INVALID_SLAVE_CASH_OPERATION());
		return false;
	}


	return true;
}

declare global {
	interface CommandType {
		endTakeOutOrder: () => Promise<Result>
	}
}

Command.register('endTakeOutOrder', Preconditions.and(Preconditions.ensure(Permission.SalesOrder_EndTakeOut), Preconditions.ensureIsNotLite), async (): Promise<Result> => {
	const ticket = SalesStore.getTicket();
	if (ticket.Customer.Id === 0) {
		await Message.show(Res.Msg.MSG_ENDT_TAKE_OUT_CUSTOMER_NOT_FOUND());
		return Result.error();
	}

	const [draft, customer] = await Promise.all([SalesOrderFacade.getDraftByTicketId(SalesStore.getTicket().Id), CustomerFacade.getForEdition(ticket.Customer.Id)]);

	if (draft.SerialNumber.Number === 0) {
		await Message.show(Res.Msg.MSG_SALES_ORDER_DRAFT_NOT_FOUND());
		return Result.error();
	}

	const data = createEndTakeOutOrderData(draft, ticket, customer);
	const methods = await CashdrawerFacade.getPaymentMethodsForPhoneOrder();

	const endTakeOutOrderResult = await Modal.show(EndTakeOutOrder, { orderData: data, availablePaymentMethods: methods });
	if (!endTakeOutOrderResult.ok)
		return Result.error();

	const endTakeoutOrderParams = toTakeOutOrderParams(draft, endTakeOutOrderResult.value);
	await SalesStore.endTakeOutSalesOrder(endTakeoutOrderParams);
	return Result.ok();
});


